import React, { useCallback, useEffect, useMemo, useState } from "react";

import { abrirArchivo, ArchivoParaVer } from "../../../../components/enrollment/visorArchivo";
import useEsTelefono from "../../../../utils/esTelefono";
import {
	AlumnoDeEntrega, ApiTareasPersonal, ArchivoDeEntrega, DetalleDeEntrega, EstadoPersonal, ListaDeEntregas
} from "../../../../services/v2/classPortal.service";
import { Aviso, Avatar, exito, Icono, LineaAviso, mensajeDeError, problema, useAlVolver } from "../comun";
import { esNotaBaja, puntosTxt } from "./comunTareas";
import "./tareas.css";

/*
	Calificar (personal, Parte B): a la izquierda los alumnos (Por Calificar, Calificadas, Por Repetir, Devueltas, Sin Entregar),
	al centro la entrega con su visor de fotos, a la derecha la nota. La rúbrica se califica por criterio (Completo, Parcial, No) y
	la nota se arma sumándolos; sin rúbrica se escribe la nota. Ninguna nota pasa de los puntos de la tarea; lo tarde se
	descuenta (% por día × días × puntos) sin dejar la nota bajo 0. Cada cambio queda en el historial con quién lo hizo.
	Parte C: Cerrar Tarea (ya nadie entrega y quien no entregó cuenta 0 en su promedio) y Reabrir Tarea.
*/

const GRUPOS: { estado: EstadoPersonal; titulo: string }[] = [
	{ estado: "por_calificar", titulo: "Por Calificar" }, { estado: "calificada", titulo: "Calificadas" }, { estado: "repetir", titulo: "Por Repetir" },
	{ estado: "devuelta", titulo: "Devueltas" }, { estado: "sin_entregar", titulo: "Sin Entregar" },
]

const nivelDe = (v: number | null | undefined, max: number): string | null => {
	if (v === undefined || v === null) return null
	if (v === 0) return "No"
	if (v === max) return "Completo"
	return v === parcial(max) ? "Parcial" : null
}

/** What the server stores when the teacher leaves the message empty (the student reads it; the teacher doesn't need it echoed). */
const NOTA_REPETIR_POR_OMISION = "Tu maestro te pidió repetir la entrega."

const ACCIONES_LOG: Record<string, string> = { reescala: "Reescalada", repetir: "Pidió repetir", devolver: "Devuelta", deshacer: "Canceló la repetición" }

const parcial = (max: number): number => (Number.isInteger(max) ? Math.ceil(max / 2) : Math.round((max / 2) * 100) / 100)

/** Lo mismo que descuenta el servidor: % por día × días × puntos de la tarea, sin pasar de lo calificado. */
const descuento = (bruta: number, pts: number, pct: number, dias: number): number =>
	dias <= 0 || pct <= 0 || bruta <= 0 ? 0 : Math.round(Math.min(bruta, (pts * Math.min(100, pct * dias)) / 100) * 100) / 100

const paraVisor = (apiT: ApiTareasPersonal, postId: number, studentId: number, f: ArchivoDeEntrega): ArchivoParaVer => ({
	nombre: f.name, mime: f.mime, origen: "Portal de Clase", urlLista: f.isImage ? f.url : null,
	obtenerUrl: async () => (await apiT.submissionFileUrl(postId, studentId, f.id)).url,
	cargar: async () => {
		const r = await fetch((await apiT.submissionFileUrl(postId, studentId, f.id)).url)
		if (!r.ok) throw new Error("No se pudo leer el archivo")
		return { blob: await r.blob(), nombre: f.name }
	},
})

interface Props { apiT: ApiTareasPersonal; postId: number; cursoTitulo: string; onCerrar: () => void; onCambio: () => void }

const Calificar: React.FC<Props> = ({ apiT, postId, cursoTitulo, onCerrar, onCambio }) => {
	const telefono = useEsTelefono()
	const [lista, setLista] = useState<ListaDeEntregas | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [aviso, setAviso] = useState<Aviso | null>(null)
	const [sel, setSel] = useState<number | null>(null)
	const [det, setDet] = useState<DetalleDeEntrega | null>(null)
	const [vista, setVista] = useState<"lista" | "detalle">("lista")
	const [criterios, setCriterios] = useState<Record<string, number | null>>({})
	const [puntos, setPuntos] = useState<string>("")
	const [comentario, setComentario] = useState<string>("")
	const [ocupado, setOcupado] = useState<boolean>(false)
	const [foto, setFoto] = useState<number>(0)
	/** "Pedir Que Repita" asks first (with the message the student will read). */
	const [pidiendoRepetir, setPidiendoRepetir] = useState<boolean>(false)
	const [notaRepetir, setNotaRepetir] = useState<string>("")

	const cargarLista = useCallback(async (): Promise<ListaDeEntregas | null> => {
		try { const l = await apiT.submissions(postId); setLista(l); setError(null); return l } catch (e) { setError(mensajeDeError(e, "No pudimos abrir la tarea.")); return null }
	}, [apiT, postId])

	const llenarForma = (d: DetalleDeEntrega) => {
		const c: Record<string, number | null> = {}
		if (d.grade?.criteria) Object.keys(d.grade.criteria).forEach((k) => { c[k] = d.grade!.criteria![k] })
		setCriterios(c)
		setPuntos(d.grade && d.grade.rawPoints !== null && !d.grade.criteria ? String(d.grade.rawPoints) : "")
		setComentario(d.grade?.comment || "")
		setFoto(0)
	}
	const abrir = useCallback(async (studentId: number) => {
		setSel(studentId); setDet(null); setAviso(null); setPidiendoRepetir(false)
		try { const d = await apiT.submission(postId, studentId); setDet(d); llenarForma(d) } catch (e) { setError(mensajeDeError(e)) }
	}, [apiT, postId])

	useEffect(() => {
		cargarLista().then((l) => {
			if (!l) return
			const primero = l.students.find((s) => s.state === "por_calificar") || l.students.find((s) => s.state === "calificada") || l.students[0]
			if (primero && !telefono) abrir(primero.id)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apiT, postId])
	useAlVolver(() => { cargarLista() })

	const tarea = lista?.task
	const rubrica = tarea?.rubric || []
	const conRubrica = rubrica.length > 0
	const bruta = conRubrica ? rubrica.reduce((a, c) => a + (criterios[String(c.id)] ?? 0), 0) : Number(puntos) || 0
	const completa = conRubrica ? rubrica.every((c) => criterios[String(c.id)] !== undefined && criterios[String(c.id)] !== null) : puntos.trim() !== "" && !isNaN(Number(puntos))
	const dias = det?.submission?.late ? det.submission.lateDays : 0
	const desc = tarea ? descuento(bruta, tarea.points, tarea.latePenaltyPct, dias) : 0
	const final = Math.max(0, Math.round((bruta - desc) * 100) / 100)
	const sobrepasa = !!tarea && !conRubrica && (Number(puntos) || 0) > tarea.points

	const alumno: AlumnoDeEntrega | undefined = lista?.students.find((s) => s.id === sel)
	const estado: EstadoPersonal | undefined = det?.state
	const enClase = tarea?.delivery === "en_clase"

	const siguiente = (l: ListaDeEntregas, actual: number): number | null => {
		const pendientes = l.students.filter((s) => (s.state === "por_calificar" || s.state === "calificada") && s.id !== actual)
		if (pendientes.length === 0) return null
		const i = l.students.findIndex((s) => s.id === actual)
		return (pendientes.find((s) => l.students.indexOf(s) > i) || pendientes[0]).id
	}

	const enviar = async (accion: "guardar" | "devolver" | "repetir" | "deshacer_repetir", irAlSiguiente: boolean) => {
		if (sel === null || !tarea || ocupado) return
		if (accion === "deshacer_repetir") {
			setOcupado(true); setAviso(null)
			try {
				const d = await apiT.grade(postId, sel, { action: "deshacer_repetir" })
				onCambio(); await cargarLista(); setDet(d); llenarForma(d)
				setAviso(exito(d.grade && d.grade.points !== null ? "Repetición cancelada: regresó su nota de " + puntosTxt(d.grade.points) + " pts." : "Repetición cancelada."))
			} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
			return
		}
		if (accion !== "repetir" && !completa) { setAviso(problema(conRubrica ? "Califica todos los criterios." : "Escribe la nota.")); return }
		if (sobrepasa) { setAviso(problema("La nota no puede pasar de " + puntosTxt(tarea.points) + " pts.")); return }
		setOcupado(true); setAviso(null)
		try {
			const crit: Record<string, number> = {}
			Object.keys(criterios).forEach((k) => { if (criterios[k] !== null && criterios[k] !== undefined) crit[k] = criterios[k] as number })
			const d = await apiT.grade(postId, sel, accion === "repetir" ? { action: "repetir", comment: notaRepetir.trim() } : { action: accion, ...(conRubrica ? { criteria: crit } : { points: Number(puntos) }), comment: comentario.trim() })
			onCambio()
			const l = await cargarLista()
			if (accion === "repetir") { setPidiendoRepetir(false); setDet(d); llenarForma(d); setAviso(exito("Le pediste que repita su entrega.")) }
			else if (irAlSiguiente && l) {
				const n = siguiente(l, sel)
				if (n !== null) { await abrir(n) } else { setDet(d); llenarForma(d); setAviso(exito("Devuelta. Ya no quedan entregas por calificar.")) }
			} else { setDet(d); llenarForma(d); setAviso(exito(accion === "devolver" ? "Devuelta" : "Guardada")) }
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}

	const devolverTodas = async () => {
		if (!lista || lista.counts.calificadas === 0) return
		if (!window.confirm("¿Devolver las " + lista.counts.calificadas + " calificadas? Los alumnos verán su nota y tu comentario.")) return
		setOcupado(true); setAviso(null)
		try {
			const r = await apiT.returnGraded(postId)
			setLista(r); onCambio(); setAviso(exito(r.returned + (r.returned === 1 ? " Devuelta" : " Devueltas")))
			if (sel !== null) { const d = await apiT.submission(postId, sel); setDet(d); llenarForma(d) }
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}

	const cerrarOReabrir = async () => {
		if (!lista) return
		const cerrar = !lista.task.closed
		if (cerrar && !window.confirm("¿Cerrar esta tarea? Ya nadie podrá entregar y quien no entregó contará 0 en su promedio. Puedes reabrirla después.")) return
		setOcupado(true); setAviso(null)
		try {
			await apiT.close(postId, cerrar)
			onCambio()
			await cargarLista()
			if (sel !== null) { const d = await apiT.submission(postId, sel); setDet(d); llenarForma(d) }
			setAviso(exito(cerrar ? "Tarea cerrada: lo que falta cuenta 0." : "Tarea reabierta: los alumnos pueden volver a entregar."))
		} catch (e) { setAviso(problema(mensajeDeError(e))) } finally { setOcupado(false) }
	}

	const recordar = async (ids?: number[]) => {
		setAviso(null)
		try {
			const r = await apiT.remind(postId, ids)
			setAviso(exito(r.sent === 0 ? (r.skippedRecent > 0 ? "Ya les recordaste hace poco." : "Nadie por recordar.") : "Recordatorio enviado a " + r.sent + (r.sent === 1 ? " alumno" : " alumnos") + "."))
		} catch (e) { setAviso(problema(mensajeDeError(e))) }
	}

	const imagenes = useMemo(() => (det?.submission?.files || []).filter((f) => f.isImage && f.url), [det])
	const otros = useMemo(() => (det?.submission?.files || []).filter((f) => !(f.isImage && f.url)), [det])
	const ampliar = (i: number) => {
		if (!det || sel === null) return
		const galeria = imagenes.map((f) => paraVisor(apiT, postId, sel, f))
		abrirArchivo({ ...galeria[i], galeria, indice: i, descargar: false })
	}

	if (error && !lista) return <div className="pc"><div className="pcPagina"><button type="button" className="pcEnlaceBtn" onClick={onCerrar}>Volver</button><div className="pcAviso pcOro" role="alert">{error}</div></div></div>
	if (!lista || !tarea) return <div className="pc"><div className="pcCargando">Cargando…</div></div>

	const c = lista.counts
	const mostrarLista = !telefono || vista === "lista"
	const mostrarDetalle = !telefono || vista === "detalle"
	const puedeRecordar = (tarea.delivery === "foto" || tarea.delivery === "archivo") && !tarea.closed

	const fila = (s: AlumnoDeEntrega) => (
		<button key={s.id} type="button" className="tkAlumnoFila" aria-current={sel === s.id ? "true" : undefined} onClick={() => { abrir(s.id); setVista("detalle") }}>
			<span className={"pcAv" + (s.state === "sin_entregar" || s.state === "repetir" ? " tkAvOro" : "")} style={{ width: 32, height: 32, fontSize: 11.5 }} aria-hidden="true">{s.photo ? <img src={s.photo} alt="" /> : s.initials}</span>
			<span className="tkNombre">{s.name}</span>
			{s.state === "devuelta" && s.points !== null ? <small style={esNotaBaja(s.points, tarea.points, lista.minGrade) ? { color: "var(--pc-oro-texto)", fontWeight: 700 } : undefined}>{puntosTxt(s.points)}</small> : null}
			{s.state === "calificada" && s.points !== null ? <small>{puntosTxt(s.points)}</small> : null}
			{s.late && s.submittedLabel ? <span className="pcChip pcChipOro" style={{ height: 22, fontSize: 11.5 }}>Tarde</span> : null}
			{s.state === "por_calificar" && s.submittedLabel && !s.late ? <small>{s.submittedLabel.split(",")[0]}</small> : null}
		</button>
	)

	const listaUi = (
		<nav aria-label="Alumnos" className="pcTarjeta tkLista">
			{GRUPOS.map((g) => {
				const alumnos = lista.students.filter((s) => s.state === g.estado)
				if (alumnos.length === 0) return null
				return (
					<div key={g.estado}>
						<div className="tkListaTitulo"><span>{g.titulo} · {alumnos.length}</span>
							{g.estado === "sin_entregar" && puedeRecordar ? <button type="button" className="pcChip pcChipOro" style={{ border: 0, cursor: "pointer", height: 30 }} onClick={() => recordar(alumnos.map((a) => a.id))}>Recordar A Todos</button> : null}
						</div>
						{alumnos.map(fila)}
					</div>
				)
			})}
			{lista.students.length === 0 ? <div className="pcVacio">Esta Tarea No Tiene Alumnos</div> : null}
		</nav>
	)

	const sinEntregar = estado === "sin_entregar"
	const cerrada = !!tarea.closed
	const puedeNota = !!det && (estado === "por_calificar" || estado === "calificada" || estado === "devuelta" || (sinEntregar && (tarea.overdue || cerrada)) || enClase
		|| (estado === "repetir" && cerrada))
	const previa = det?.submission?.previousGrade || null
	const historial = det && det.log.length > 0 ? (
		<div className="pcTarjeta" style={{ padding: 16 }}>
			<div className="pcEtiqueta">Historial De La Nota</div>
			<div className="tkHistorial">
				{det.log.map((l, i) => <div key={i}>{l.whenLabel} · {l.by} · {ACCIONES_LOG[l.action] || "Nota"} {l.before === null ? "" : puntosTxt(l.before)}{l.after !== null ? (l.before === null ? "" : " → ") + puntosTxt(l.after) : ""}</div>)}
			</div>
		</div>
	) : null
	const cancelarRepeticion = () => {
		if (!window.confirm(previa ? "¿Cancelar la repetición? Regresa su nota de " + puntosTxt(previa.points) + " pts." : "¿Cancelar la repetición? Su entrega vuelve a Por Calificar.")) return
		enviar("deshacer_repetir", false)
	}
	const nombrePila = alumno?.name.split(" ")[0] || ""

	const centro = det && alumno ? (
		<section aria-label="Entrega" className="tkCentro">
			{telefono ? <button type="button" className="pcEnlaceBtn tkAtras" onClick={() => setVista("lista")}><Icono n="atras" size={18} />Alumnos</button> : null}
			<div className="pcTarjeta">
				<div className="pcFilaAutor">
					<Avatar a={{ name: alumno.name, initials: alumno.initials, photo: alumno.photo }} size={40} />
					<div className="pcAutorTextos">
						<div className="pcAutorNombre">{alumno.name}</div>
						<div className="pcTenue pcChico">
							{enClase ? "Se califica en clase" : det.submission && det.submission.submittedLabel ? "Entregó " + det.submission.submittedLabel + " · " + (det.submission.late ? "Tarde " + det.submission.lateDays + (det.submission.lateDays === 1 ? " día" : " días") : "A Tiempo") : sinEntregar ? "No ha entregado" : ""}
						</div>
					</div>
					{det.submission && det.submission.files.length > 0 ? <span className="pcChip">{det.submission.files.length} {det.submission.files.length === 1 ? "Archivo" : "Archivos"}</span> : null}
					{det.submission?.late ? <span className="pcChip pcChipOro">Tarde</span> : null}
				</div>
				{estado === "repetir" ? <div className="tkAvisoOro">Esperando que repita su entrega.{det.submission?.repeatNote && det.submission.repeatNote !== NOTA_REPETIR_POR_OMISION ? <> Le escribiste: “{det.submission.repeatNote}”</> : null}</div> : null}
				{imagenes.length > 0 ? (
					<>
						<div className="tkVisor">
							<img src={imagenes[Math.min(foto, imagenes.length - 1)].url || ""} alt={"Foto " + (foto + 1) + " de " + imagenes.length + " de " + alumno.name} />
							<button type="button" className="tkAmpliar" aria-label="Ampliar" onClick={() => ampliar(Math.min(foto, imagenes.length - 1))}><Icono n="ampliar" size={20} /></button>
						</div>
						{imagenes.length > 1 ? (
							<div className="tkMiniaturas">
								{imagenes.map((f, i) => (
									<button key={f.id} type="button" className="tkMiniatura" aria-pressed={i === foto} aria-label={"Ver foto " + (i + 1)} onClick={() => setFoto(i)}><img src={f.url || ""} alt="" /></button>
								))}
							</div>
						) : null}
					</>
				) : null}
				{otros.length > 0 ? (
					<div className="pcArchivos">
						{otros.map((f) => <button key={f.id} type="button" className="pcArchivo" onClick={() => abrirArchivo(paraVisor(apiT, postId, sel as number, f))}><Icono n="doc" /><span className="pcNombre">{f.name}</span></button>)}
					</div>
				) : null}
				{det.submission?.comment ? <div className="tkComentarioAlumno"><b>{nombrePila}:</b> {det.submission.comment}</div> : null}
				{sinEntregar && !tarea.overdue && !cerrada ? (
					<div className="pcTenue">Aún no entrega y la tarea no ha vencido.{puedeRecordar ? <> <button type="button" className="tkLink" onClick={() => recordar([alumno.id])}>Recordar</button></> : null}</div>
				) : null}
				{sinEntregar && (tarea.overdue || cerrada) ? (
					<div className="tkAvisoOro">No entregó{cerrada ? " y la tarea está cerrada: cuenta 0" : ""}. Puedes devolverle su nota (normalmente 0).{puedeRecordar ? <> <button type="button" className="tkLink" onClick={() => recordar([alumno.id])}>Recordar</button></> : null}</div>
				) : null}
			</div>
		</section>
	) : <section className="tkCentro"><div className="pcCargando">{sel === null ? "Elige un alumno" : "Cargando…"}</div></section>

	const notaUi = det && alumno && puedeNota ? (
		<aside aria-label="Nota" className="tkNota">
			<div className="pcTarjeta" style={{ padding: 18, gap: 14 }}>
				<div className="tkNotaTotal">
					<span className="pcEtiqueta">Nota</span>
					<span className={"tkNumeroGrande" + (completa && esNotaBaja(final, tarea.points, lista.minGrade) ? " tkNumeroOro" : "")}>{completa || bruta > 0 ? puntosTxt(final) : "–"}</span>
					<span className="pcTenue" style={{ fontWeight: 700 }}>/ {puntosTxt(tarea.points)}</span>
				</div>
				{desc > 0 ? <div className="tkAvisoOro">Entrega tarde ({dias} {dias === 1 ? "día" : "días"} × {puntosTxt(tarea.latePenaltyPct)}%): se restan {puntosTxt(desc)} pts de {puntosTxt(bruta)}.</div> : null}
				{conRubrica ? rubrica.map((r) => {
					const v = criterios[String(r.id)]
					const niveles: [string, number][] = [["Completo", r.points], ["Parcial", parcial(r.points)], ["No", 0]]
					return (
						<div className="tkCriterioNota" key={r.id}>
							<div><span>{r.name}</span><span className="pcTenue">{v === undefined || v === null ? "–" : puntosTxt(v)} / {puntosTxt(r.points)}</span></div>
							<div className="tkNiveles" role="group" aria-label={"Nivel de " + r.name}>
								{niveles.map(([texto, pts]) => <button key={texto} type="button" className="tkNivel" aria-pressed={nivelDe(v, r.points) === texto} onClick={() => setCriterios({ ...criterios, [String(r.id)]: pts })}>{texto}</button>)}
							</div>
						</div>
					)
				}) : (
					<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="clPuntos">Puntos (De {puntosTxt(tarea.points)})</label>
						<input id="clPuntos" className="pcInput" inputMode="decimal" value={puntos} onChange={(e) => setPuntos(e.target.value)} />
						{sobrepasa ? <div className="pcAviso" style={{ color: "var(--pc-oro-texto)" }}>No puede pasar de {puntosTxt(tarea.points)} pts.</div> : null}</div>
				)}
				<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="clComentario">Comentario Para {nombrePila}</label>
					<textarea id="clComentario" className="pcTextarea" rows={3} maxLength={2000} value={comentario} onChange={(e) => setComentario(e.target.value)} /></div>
				<LineaAviso a={aviso} />
				<div className="tkBotonesNota">
					{estado === "devuelta" ? (
						<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => enviar("devolver", false)}>Guardar Cambios</button>
					) : (
						<>
							<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => enviar("devolver", true)}>Devolver Y Siguiente</button>
							{!sinEntregar && estado !== "repetir" ? <button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => enviar("guardar", false)}>Guardar</button> : null}
							{estado === "repetir" ? <button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={cancelarRepeticion}>Cancelar Repetición</button> : null}
						</>
					)}
					{!enClase && !sinEntregar && estado !== "repetir" && !!det.submission?.submittedLabel && !pidiendoRepetir
						? <button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => { setNotaRepetir(comentario.trim()); setPidiendoRepetir(true); setAviso(null) }}>Pedir Que Repita</button> : null}
				</div>
				{pidiendoRepetir ? (
					<div className="tkConfirmar" role="group" aria-label="Pedir que repita">
						<div className="pcT" style={{ fontSize: 17, fontWeight: 600 }}>¿Pedirle Que Repita?</div>
						<div className="pcTenue pcChico">{det.grade && det.grade.points !== null
							? "Su nota de " + puntosTxt(det.grade.points) + " pts se guarda: puedes regresarla con Cancelar Repetición mientras no vuelva a entregar."
							: "Puedes cancelarlo mientras no vuelva a entregar."}</div>
						<div className="pcCampo"><label className="pcCampoEtiqueta" htmlFor="clRepetir">Mensaje Para {nombrePila}</label>
							<textarea id="clRepetir" className="pcTextarea" rows={3} maxLength={2000} placeholder="Qué debe corregir" value={notaRepetir} onChange={(e) => setNotaRepetir(e.target.value)} autoFocus /></div>
						<div className="tkBotonesNota">
							<button type="button" className="pcBtn pcBtnP" disabled={ocupado} onClick={() => enviar("repetir", false)}>Pedir Que Repita</button>
							<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={() => setPidiendoRepetir(false)}>Cancelar</button>
						</div>
					</div>
				) : null}
				{estado === "repetir" && previa ? <div className="pcTenue pcChico">Nota antes de pedirle repetir: <b>{puntosTxt(previa.points)} pts</b></div> : null}
				{det.grade?.gradedBy ? <div className="pcTenue pcChico">Calificó {det.grade.gradedBy}{det.grade.returned ? " · Ya la ve el alumno" : " · El alumno aún no la ve"}</div> : null}
			</div>
			{historial}
		</aside>
	) : det && alumno && estado === "repetir" ? (
		<aside aria-label="Nota" className="tkNota">
			<div className="pcTarjeta" style={{ padding: 18, gap: 12 }}>
				<span className="pcEtiqueta">Por Repetir</span>
				{previa ? (
					<>
						<div className="tkNotaTotal">
							<span className="pcTenue" style={{ fontWeight: 700 }}>Nota Anterior</span>
							<span className={"tkNumeroGrande" + (esNotaBaja(previa.points, tarea.points, lista.minGrade) ? " tkNumeroOro" : "")}>{puntosTxt(previa.points)}</span>
							<span className="pcTenue" style={{ fontWeight: 700 }}>/ {puntosTxt(tarea.points)}</span>
						</div>
						{previa.comment ? <div className="pcTenue pcChico" style={{ overflowWrap: "anywhere" }}>Tu comentario: {previa.comment}</div> : null}
					</>
				) : <div className="pcTenue">No tenía nota cuando le pediste repetir.</div>}
				<div className="pcTenue pcChico">Cuando vuelva a entregar, aparece en Por Calificar.</div>
				<LineaAviso a={aviso} />
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={cancelarRepeticion}>Cancelar Repetición</button>
			</div>
			{historial}
		</aside>
	) : null

	return (
		<div className="pc">
			<header className="pcCabeza">
				<button type="button" className="pcIb" aria-label="Volver a las tareas" onClick={onCerrar}><Icono n="atras" size={22} /></button>
				<div className="pcCabezaTitulo"><div className="pcT" style={{ overflowWrap: "anywhere" }}>{tarea.title}</div>
					<div className="pcTenue pcChico">{[cursoTitulo, c.porCalificar + " Por Calificar", c.calificadas + " Calificadas"].join(" · ")}{cerrada ? <> · <span className="pcChip pcChipOro" style={{ height: 22, fontSize: 11.5 }}>Cerrada</span></> : null}</div></div>
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado} onClick={cerrarOReabrir} title={cerrada ? "Los alumnos podrán volver a entregar" : "Nadie podrá entregar; quien no entregó cuenta 0"}>{cerrada ? "Reabrir Tarea" : "Cerrar Tarea"}</button>
				<button type="button" className="pcBtn pcBtnS" disabled={ocupado || c.calificadas === 0} onClick={devolverTodas}>Devolver Todas Las Calificadas</button>
			</header>
			<main className="tkCalificar">
				{aviso && (!det || !notaUi) ? <LineaAviso a={aviso} style={{ width: "100%" }} /> : null}
				{mostrarLista ? listaUi : null}
				{mostrarDetalle ? <>{centro}{notaUi}</> : null}
			</main>
		</div>
	)
}

export default Calificar
